import React, { useEffect, useState } from "react";

import { CalendarMonth, KeyboardArrowDown } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Modal } from "app/components/Modal";
import SearchableSelect from "app/components/SearchableSelect";
import {
  selectOptionalInstructorOptions,
  selectTreatmentBankDetailsEdited,
  selectTreatmentTypesOptions,
} from "app/containers/TreatmentBanks/redux/selector";
import { actions } from "app/containers/TreatmentBanks/redux/slice";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectList } from "../../../../redux/selector";
import { useStyles } from "./styles";
import { ErrorsType, onValidate } from "./validator";

const SELECT_ALL_VALUE = "__SELECT_ALL__";

export const AddTreatmentBank = () => {
  const dispatch = useDispatch();
  const treatmentTypes = useSelector(selectTreatmentTypesOptions);
  const optionalInstructiorTypes = useSelector(selectOptionalInstructorOptions);
  const listitems = useSelector(selectList);
  const [errors, setErrors] = useState<ErrorsType>({
    treatmentId: "",
    startDate: "",
    endDate: "",
    max: "",
    instructorName: "",
  });

  const {
    modalName,
    open,
    startDate,
    endDate,
    treatmentId,
    treatmentName,
    instructorName,
    max,
    isMust,
    id,
    idx,
  } = useSelector(selectTreatmentBankDetailsEdited);
  const styles = useStyles();
  const { t, i18n } = useTranslation();

  const isEdit = modalName.includes("EDIT");
  const isPersistedEdit = isEdit && !!id;
  const disabled = isPersistedEdit;

  useEffect(() => {
    if (!open) {
      setErrors({
        treatmentId: "",
        startDate: "",
        endDate: "",
        max: "",
        instructorName: "",
      });
    }
  }, [open]);

  const handleSave = () => {
    const errors = onValidate({
      treatmentId,
      startDate: startDate ?? "",
      endDate: endDate ?? "",
      max: parseInt(max ?? "0"),
      instructorName: instructorName,
    });
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }
    if (!disabled) {
      const isExist = listitems?.editItems?.some((item) => {
        const isSameEditedRow = isEdit && Number(item.idx) === Number(idx);
        return (
          !isSameEditedRow &&
          item.action !== "delete" &&
          Number(item.treatmentId) === Number(treatmentId)
        );
      });
      if (isExist) {
        setErrors({
          ...errors,
          treatmentId: "TREATMENT_BANKS.TREATMENT_ALREADY_EXIST",
        });
        return;
      }
    }

    dispatch(
      actions.createTreatmentBankTreatment({
        id,
        idx,
        treatmentId,
        instructorName,
        startDate,
        endDate,
        max,
        treatmentName,
        isMust,
        isEdit,
        instructorList: optionalInstructiorTypes,
        type: treatmentTypes.find((x) => x.id == treatmentId)?.type,
      })
    );
  };

  const handleClose = () => {
    dispatch(actions.resetTreatmentBankDetailsEditedData());
  };

  const direction = useTheme().direction;

  const onChangeHandler = ({ key, value }) => {
    setErrors({
      ...errors,
      [key]: "",
    });
    dispatch(
      actions.setTreatmentBankDetailsEditedData({
        key,
        value,
      })
    );
    if (key === "treatmentId") {
      dispatch(
        actions.setTreatmentBankDetailsEditedData({
          key: "instructorName",
          value: [],
        })
      );
      dispatch(actions.getOptionInstructorOptions(value));
      if (parseInt(value) > 0 && treatmentTypes.length > 0) {
        dispatch(
          actions.setTreatmentBankDetailsEditedData({
            key: "treatmentName",
            value: treatmentTypes.filter((x) => x.id == value)[0].name,
          })
        );
      }
    }
  };
  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        className={styles.messageModal}
        sx={{
          direction,
        }}
      >
        <div className={styles.modalContainer}>
          <div>
            <h1 className={styles.treatment_heading}>
              {t(`TREATMENT_BANKS.${modalName}`)}{" "}
            </h1>
          </div>
          <Grid container justifyContent={"space-between"}>
            <Grid item xs={5.5}>
              <label className={styles.label}>
                {t("TREATMENT_BANKS.TREATMENT_TYPE_NAME")}
              </label>
              <SearchableSelect
                disabled={disabled}
                value={treatmentId}
                className={clsx(
                  styles.textEnabled,
                  disabled && styles.textDisabled
                )}
                onChange={(val) =>
                  onChangeHandler({
                    key: "treatmentId",
                    value: val as any,
                  })
                }
                error={!!errors?.treatmentId}
                searchable
                options={treatmentTypes.map((treatmentType: any) => ({
                  label: treatmentType.name,
                  value: treatmentType.id,
                }))}
              />
              {errors?.treatmentId && (
                <span className={styles.error}>{t(errors?.treatmentId)}</span>
              )}
            </Grid>
            <Grid item xs={5.5}>
              <label className={styles.label}>
                {t("TREATMENT_BANKS.OPTIONAL_INSTRUCTORS")} (
                {t("TREATMENT_BANKS.MULTI_SELECT")})
              </label>
              {(() => {
                const lockedIds = isPersistedEdit
                  ? optionalInstructiorTypes
                      .filter((opt) => instructorName.includes(opt.id))
                      .map((opt) => opt.id)
                  : [];
                const selectableIds = optionalInstructiorTypes
                  .filter((opt) => !lockedIds.includes(opt.id))
                  .map((opt) => opt.id);
                const selectableChecked = selectableIds.filter((id) =>
                  instructorName.includes(id),
                );
                const allSelectableChecked =
                  selectableIds.length > 0 &&
                  selectableChecked.length === selectableIds.length;
                const someSelectableChecked =
                  selectableChecked.length > 0 && !allSelectableChecked;
                return (
                  <TextField
                    select
                    fullWidth
                    margin="dense"
                    required
                    autoComplete="off"
                    variant="outlined"
                    error={!!errors?.instructorName}
                    value={instructorName}
                    onChange={(e) => {
                      const raw = e.target.value as unknown as
                        | string[]
                        | string;
                      const values = Array.isArray(raw) ? raw : [raw];
                      if (values.includes(SELECT_ALL_VALUE)) {
                        const next = allSelectableChecked
                          ? lockedIds
                          : Array.from(
                              new Set([...lockedIds, ...selectableIds]),
                            );
                        onChangeHandler({
                          key: "instructorName",
                          value: next,
                        });
                        return;
                      }
                      onChangeHandler({
                        key: "instructorName",
                        value: values.filter((v) => v !== SELECT_ALL_VALUE),
                      });
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFF",
                      },
                    }}
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      IconComponent: KeyboardArrowDown,
                      renderValue: (selected) =>
                        (selected as string[])
                          .filter((id) => id !== SELECT_ALL_VALUE)
                          .map(
                            (id) =>
                              optionalInstructiorTypes.find(
                                (opt) => opt.id === id,
                              )?.name ?? id,
                          )
                          .join(", "),
                    }}
                  >
                    <MenuItem
                      key={SELECT_ALL_VALUE}
                      value={SELECT_ALL_VALUE}
                      disabled={selectableIds.length === 0}
                    >
                      <Checkbox
                        checked={allSelectableChecked}
                        indeterminate={someSelectableChecked}
                      />
                      <ListItemText primary={t("COMMON.SELECT_ALL")} />
                    </MenuItem>
                    {optionalInstructiorTypes.map((e) => (
                      <MenuItem
                        key={e.id}
                        value={e.id}
                        disabled={
                          isPersistedEdit && instructorName.includes(e.id)
                        }
                      >
                        <Checkbox checked={instructorName.includes(e.id)} />
                        <ListItemText primary={e.name} />
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })()}
              {errors?.instructorName && (
                <span className={styles.error}>
                  {t(errors?.instructorName)}
                </span>
              )}
            </Grid>
          </Grid>
          <Grid style={{ marginTop: "5px" }} container spacing={2}>
            <Grid item xs={3.5}>
              <label className={styles.label}>
                {t("TREATMENT_BANKS.START_DATE")}
              </label>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={disabled}
                  className={clsx(
                    styles.dateArea,
                    disabled && styles.textDisabled
                  )}
                  views={["day", "month", "year"]}
                  value={startDate}
                  minDate={new Date()}
                  onChange={(newValue) => {
                    onChangeHandler({
                      key: "startDate",
                      value: newValue,
                    });
                  }}
                  components={{
                    OpenPickerIcon: CalendarMonth,
                  }}
                  OpenPickerButtonProps={{
                    style: { color: disabled ? "#ccc" : "#012397" },
                  }}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="dense"
                      helperText={null}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "__/__/____",
                      }}
                      error={!!errors?.startDate}
                    />
                  )}
                />
              </LocalizationProvider>
              {errors?.startDate && (
                <span className={styles.error}>{t(errors?.startDate)}</span>
              )}
            </Grid>
            <Grid item xs={3.5}>
              <label className={styles.label}>
                {t("TREATMENT_BANKS.END_DATE")}
              </label>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={disabled}
                  className={clsx(
                    styles.dateArea,
                    disabled && styles.textDisabled
                  )}
                  views={["day", "month", "year"]}
                  value={endDate}
                  minDate={new Date()}
                  onChange={(newValue) => {
                    onChangeHandler({
                      key: "endDate",
                      value: newValue,
                    });
                  }}
                  OpenPickerButtonProps={{
                    style: { color: disabled ? "#ccc" : "#012397" },
                  }}
                  components={{
                    OpenPickerIcon: CalendarMonth,
                  }}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="dense"
                      helperText={null}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "__/__/____",
                      }}
                      error={!!errors?.endDate}
                    />
                  )}
                />
              </LocalizationProvider>
              {errors?.endDate && (
                <span className={styles.error}>{t(errors?.endDate)}</span>
              )}
            </Grid>
            <Grid item xs={5}>
              <label className={styles.label}>
                {t("TREATMENT_BANKS.MAXIMUM_PER_PATIENT")}
              </label>
              <TextField
                disabled={disabled}
                margin="dense"
                fullWidth
                variant="outlined"
                required
                InputProps={{ inputProps: { min: 1, max: 100 } }}
                type="number"
                value={max}
                onChange={(e) => {
                  onChangeHandler({
                    key: "max",
                    value: e.target.value,
                  });
                }}
                autoComplete={"off"}
                className={clsx(
                  styles.dateArea,
                  disabled && styles.textDisabled
                )}
                error={!!errors?.max}
                helperText={errors?.max && t(errors?.max)}
                FormHelperTextProps={{
                  style: {
                    textAlign: i18n.dir() === "ltr" ? "left" : "right",
                  },
                }}
                placeholder={t("TREATMENT_BANKS.ENTER_MAXIMUM_PER_PATIENT")}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={3.5}>
              <FormControlLabel
                disabled={disabled}
                style={{ margin: "10px 0px -10px -10px" }}
                control={<Checkbox checked={isMust} color="info" />}
                label={t("TREATMENT_BANKS.IS_MUST")}
                className={clsx(disabled && styles.textDisabled)}
                value={isMust}
                onChange={(e) => {
                  onChangeHandler({
                    key: "isMust",
                    value: !isMust,
                  });
                }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
              gap: "10px",
            }}
          >
            <Button
              className={styles.cancelBtn}
              variant="outlined"
              onClick={handleClose}
            >
              {t("BUTTONS.CANCEL")}
            </Button>
            <Button
              className={styles.saveBtn}
              variant="contained"
              onClick={() => handleSave()}
              sx={{
                "&.Mui-disabled": {
                  color: "#ffffff",
                },
              }}
            >
              {t("BUTTONS.SAVE")}
            </Button>
          </Box>
        </div>
      </Card>
    </Modal>
  );
};
